<template>
  <div :style="{ maxHeight: '800px', overflowY: 'auto' }">
    <shift-auth-deny-form
      ref="shiftAuthDenyForm"
      :visible.sync="visibleDenyReason"
      :reason.sync="declineComment"
      :is-adjusted="true"
      :shift="shift"
      :authorizations="authorizations.filter(auth => selectedAuthorizations.includes(auth.id))"
      @confirmDeny="confirmDeny"
    />

    <shift-auth-accept
      :visible.sync="visibleAcceptComment"
      :comment.sync="acceptComment"
      :loading="loadingAccept"
      :show-adjust="true"
      test-id-prefix="shift-view-authorizations"
      @plain-accept="confirmAccept"
      @disciplinary-accept="confirmAccept"
      @accept-and-adjust="confirmAcceptAndAdjust"
    />

    <div class="flex flex-column gap-3">
      <div class="flex justify-content-between">
        <h3 class="m-0">Violations</h3>

        <div
          class="flex gap-2"
          v-if="
            !loadError &&
            shift &&
            hasUncontrolledPermission('manage_authorizations', {
              id: shift.internal_payment_info.associate.id
            }) &&
            !isBlockedActions
          "
        >
          <a-button
            type="danger"
            :loading="loadingDecline"
            :disabled="selectedAuthorizations.length == 0 || loadingAccept"
            @click="selectedAuthorizations.length > 1 ? denyAuthorizations() : (visibleDenyReason = true)"
          >
            Deny
          </a-button>
          <a-button
            type="primary"
            :loading="loadingAccept"
            :disabled="selectedAuthorizations.length == 0 || loadingDecline"
            @click="visibleAcceptComment = true"
            data-testid="shift-view-authorizations-open-accept-modal-button"
          >
            Accept
          </a-button>
        </div>
      </div>

      <a-alert
        v-if="loadError"
        type="error"
        show-icon
        message="Violations couldn't be loaded. Close and reopen this shift to try again."
        data-testid="shift-view-authorizations-error"
      />
      <a-alert
        v-else
        :type="authorizationSummary.type"
        show-icon
        :message="authorizationSummary.message"
        data-testid="shift-view-authorizations-summary"
      />

      <template v-if="!loadError && (actualAuthorizations.length || resolvedAuthorizations.length)">
        <shift-facility-administrator-contact
          v-if="showAdministratorContact"
          :associate-id="shift.internal_payment_info.associate.id"
        />
        <div
          class="flex gap-2 align-items-center"
          v-if="
            !loadError &&
            shift &&
            hasUncontrolledPermission('manage_authorizations', {
              id: shift.internal_payment_info.associate.id
            }) &&
            !isBlockedActions
          "
        >
          <a-checkbox
            @change="onSelectAllChange"
            :checked="selectedAuthorizations.length === authorizations.length"
            :indeterminate="indeterminate"
          />
          <h4 class="m-0">Select All</h4>
        </div>
        <a-tabs v-model="activeListKey">
          <a-tab-pane
            v-if="actualAuthorizations.length"
            key="actual"
            :tab="`Unreviewed (Total: ${actualAuthorizations.length})`"
          >
            <shift-auth-list-modal
              :auths-list="actualAuthorizations"
              :associate="shift.internal_payment_info.associate"
              :version_created="shift.version_created_at"
              :selected-auths="selectedAuthorizations"
              :actions-blocked="isBlockedActions"
              :initial-page="actualInitialPage"
              @change="setAuthorizations"
              @delete="afterAuthDelete"
            />
          </a-tab-pane>
          <a-tab-pane key="resolved" :tab="`Resolved (Total: ${resolvedAuthorizations.length})`">
            <shift-auth-list-modal
              v-if="resolvedAuthorizations.length"
              :auths-list="resolvedAuthorizations"
              :associate="shift.internal_payment_info.associate"
              :version_created="shift.version_created_at"
              :selected-auths="selectedAuthorizations"
              :actions-blocked="isBlockedActions"
              :initial-page="resolvedInitialPage"
              @change="setAuthorizations"
              @delete="afterAuthDelete"
            />
            <span v-else>No Resolved Violations Found</span>
          </a-tab-pane>
        </a-tabs></template
      >
    </div>
  </div>
</template>

<script>
import ShiftAuthDenyForm from '@/modules/authorizations/components/shift-auth-deny.vue'
import ShiftAuthAccept from '@/modules/authorizations/components/shift-auth-accept.vue'
import ShiftAuthListModal from '../../../authorizations/components/table/shift-auth-list.vue'
import ShiftFacilityAdministratorContact from '../../../authorizations/components/shift-facility-administrator-contact.vue'
import { createNamespacedHelpers } from 'vuex'
import { Alert, Button, Checkbox, notification, Tabs } from 'ant-design-vue'
import AuthReasonMixin from '../../../authorizations/mixins/AuthReasonMixin.js'
import permissionMixin from '@/mixins/permissionMixin'
import { AUTH_STATUSES } from '@/store/modules/shifts/constants'

const { mapActions, mapState } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    'a-checkbox': Checkbox,
    'shift-auth-list-modal': ShiftAuthListModal,
    'shift-auth-deny-form': ShiftAuthDenyForm,
    'shift-auth-accept': ShiftAuthAccept,
    'shift-facility-administrator-contact': ShiftFacilityAdministratorContact
  },
  mixins: [AuthReasonMixin, permissionMixin],
  data() {
    return {
      visibleDenyReason: false,
      visibleAcceptComment: false,

      selectedAuthorizations: [],

      loadingDecline: false,
      loadingAccept: false,

      withAdjust: false,

      acceptComment: undefined,
      declineComment: undefined,
      activeListKey: 'actual',
      actualInitialPage: 0,
      resolvedInitialPage: 0
    }
  },
  props: {
    focusedAuthorizationId: {
      type: Number,
      default: undefined
    },
    loadError: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update'],
  computed: {
    ...mapState({
      authorizations: state => state.shiftAuthorizations,
      shift: state => state.shift.shift,
      associate: state => state.shift.associate
    }),
    showAdministratorContact() {
      return (
        !!this.shift?.internal_payment_info?.associate?.id &&
        (this.isBlockedActions ||
          !this.hasUncontrolledPermission('manage_authorizations', {
            id: this.shift.internal_payment_info.associate.id
          }))
      )
    },
    indeterminate() {
      return !!this.selectedAuthorizations.length && this.selectedAuthorizations.length < this.authorizations.length
    },

    fullSelectedAuths() {
      if (this.authorizations.length === 0 || this.authorizations.length === 0) {
        return []
      }

      return this.authorizations.filter(auth => this.selectedAuthorizations.includes(auth.id))
    },

    isBlockedActions() {
      return (
        this.$store.state.applicationState.currentProject.staffing &&
        this.shift?.external_payment_info &&
        this.shift?.external_payment_info?.project?.id !== this.$store.state.applicationState.currentProject.id
      )
    },

    actualAuthorizations() {
      return this.authorizations.filter(item => !item.deleted_at && item.status === 'initial')
    },
    resolvedAuthorizations() {
      return this.authorizations.filter(item => item.deleted_at || item.status !== 'initial')
    },
    focusedAuthorization() {
      return this.authorizations.find(item => item.id === this.focusedAuthorizationId)
    },
    extendedShiftAuthTypes() {
      return this.$store.state.applicationState.extendedShiftAuthTypes || []
    },
    authorizationSummary() {
      const payroll = this.actualAuthorizations.filter(
        item => this.getAuthorizationCategory(item.type) === 'payroll'
      ).length
      const disciplinary = this.actualAuthorizations.length - payroll
      const unresolvedCount = this.actualAuthorizations.length
      const resolvedCount = this.resolvedAuthorizations.length
      const declinedCount = this.resolvedAuthorizations.filter(
        item => item.status === 'declined' && !item.deleted_at
      ).length

      if (!unresolvedCount && !resolvedCount) {
        return {
          type: 'success',
          message: 'No violations on this shift. Nothing here needs your review.'
        }
      }

      if (!unresolvedCount && declinedCount) {
        return {
          type: 'warning',
          message: `All ${resolvedCount} ${this.pluralizeAuthorization(
            resolvedCount
          )} on this shift are resolved, but ${declinedCount} were declined. Payment can't be completed while a declined violation stays on the shift.`
        }
      }

      if (!unresolvedCount) {
        return {
          type: 'success',
          message: `All ${resolvedCount} ${this.pluralizeAuthorization(
            resolvedCount
          )} on this shift are resolved. Nothing here needs your review.`
        }
      }

      if (resolvedCount && payroll && disciplinary) {
        return {
          type: 'error',
          message: `This shift has ${unresolvedCount} ${this.pluralizeAuthorization(
            unresolvedCount
          )} waiting for your review, including ${payroll} payroll, and ${resolvedCount} already resolved. Payment can't be completed until the remaining ones are resolved.`
        }
      }

      if (resolvedCount && payroll) {
        return {
          type: 'error',
          message: `This shift has ${payroll} payroll ${this.pluralizeAuthorization(
            payroll
          )} waiting for your review, and ${resolvedCount} already resolved. Payment can't be completed until the remaining ones are resolved.`
        }
      }

      if (resolvedCount) {
        return {
          type: 'warning',
          message: `This shift has ${disciplinary} disciplinary ${this.pluralizeAuthorization(
            disciplinary
          )} waiting for your review, and ${resolvedCount} already resolved.`
        }
      }

      if (payroll && disciplinary) {
        return {
          type: 'error',
          message: `This shift has ${unresolvedCount} ${this.pluralizeAuthorization(
            unresolvedCount
          )} waiting for your review, including ${payroll} payroll. Payment can't be completed until they're resolved.`
        }
      }

      if (payroll) {
        return {
          type: 'error',
          message: `This shift has ${payroll} payroll ${this.pluralizeAuthorization(payroll)} waiting for your review. Payment can't be completed until they're resolved.`
        }
      }

      return {
        type: 'warning',
        message: `This shift has ${disciplinary} disciplinary ${this.pluralizeAuthorization(disciplinary)} waiting for your review.`
      }
    }
  },
  watch: {
    focusedAuthorization: {
      handler: 'focusAuthorization',
      immediate: true
    },
    actualAuthorizations: {
      handler(actual) {
        if (!this.focusedAuthorizationId) {
          this.activeListKey = actual.length ? 'actual' : 'resolved'
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['fetchShiftAuthorizations', 'acceptShiftAuthorization', 'declineShiftAuthorization']),

    pluralizeAuthorization(count) {
      return count === 1 ? 'violation' : 'violations'
    },

    getAuthorizationCategory(type) {
      const configuredType = this.extendedShiftAuthTypes.find(item => item.key === type)?.auth_type
      return configuredType ?? AUTH_STATUSES[type]?.type ?? 'disciplinary'
    },

    focusAuthorization(authorization) {
      if (!authorization) return

      const actualIndex = this.actualAuthorizations.findIndex(item => item.id === authorization.id)
      if (actualIndex >= 0) {
        this.actualInitialPage = actualIndex
        this.activeListKey = 'actual'
        return
      }

      const resolvedIndex = this.resolvedAuthorizations.findIndex(item => item.id === authorization.id)
      if (resolvedIndex >= 0) {
        this.resolvedInitialPage = resolvedIndex
        this.activeListKey = 'resolved'
      }
    },

    // modals
    openAcceptModal() {
      this.visibleAcceptComment = true
    },

    onSelectAllChange() {
      if (this.selectedAuthorizations.length === this.authorizations.length) {
        this.selectedAuthorizations = []
      } else {
        this.selectedAuthorizations = this.authorizations.map(item => item.id)
      }
    },

    // setters

    setAuthorizations(authorizations) {
      this.selectedAuthorizations = authorizations
    },

    cancelDeny() {
      this.visibleDenyReason = false
      this.declineComment = undefined
    },

    confirmDeny(withAdjust) {
      this.visibleDenyReason = false

      if (this.declineComment && this.selectedAuthorizations && this.selectedAuthorizations.length > 0) {
        this.withAdjust = withAdjust
        this.denyAuthorizations()
      } else {
        notification.warning({
          message: 'Error',
          description: 'Please fill deny reason and try again'
        })
      }
    },

    confirmAccept(disciplinaryActions) {
      if (this.selectedAuthorizations && this.selectedAuthorizations.length > 0) {
        this.acceptAuthorizations(disciplinaryActions)
      }
    },

    confirmAcceptAndAdjust() {
      if (this.selectedAuthorizations && this.selectedAuthorizations.length > 0) {
        this.acceptAuthorizations(undefined, true)
      }
    },

    afterAuthDelete(id) {
      this.authorizations = this.authorizations.filter(item => item.id !== id)

      this.$emit('update', false)
    },

    async acceptAuthorizations(disciplinaryActions, withAdjust = false) {
      if (this.loadingAccept) return

      this.withAdjust = withAdjust
      this.loadingAccept = true
      try {
        const payload = {
          auth_ids: this.selectedAuthorizations,
          comment: this.acceptComment,
          ...(disciplinaryActions?.length ? { disciplinary_actions: disciplinaryActions } : {}),
          review_context: this.fullSelectedAuths.map(authorization => ({ authorization, associate: this.associate }))
        }

        const { cancelling } = await this.acceptShiftAuthorization(payload)

        if (cancelling) this.loadingAccept = false
        else {
          this.showNotification('success', 'Success', 'Violations accepted successfully')

          this.visibleAcceptComment = false
          this.acceptComment = ''
          this.$emit('update', this.withAdjust)
        }
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingAccept = false
        this.withAdjust = false
      }
    },

    async denyAuthorizations() {
      this.loadingDecline = true
      try {
        const payload = {
          auth_ids: this.selectedAuthorizations,
          comment:
            this.selectedAuthorizations.length > 1
              ? this.fullSelectedAuths.map(item => this.getFullReason(item.type, this.shift))
              : this.selectedAuthorizations.map(() => this.declineComment)
        }

        await this.declineShiftAuthorization(payload)

        this.showNotification('success', 'Success', 'Violations declined successfully')
        this.$emit('update', this.withAdjust)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingDecline = false
        this.withAdjust = false
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    }
  }
}
</script>

<style scoped>
.wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}
</style>
